import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnack } from "../../../hooks/useSnack";
import lessonServices from "../../../services/lessonServices";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import Modal from "../../Modal";

const ConfirmUnlockLockLessonsModal = forwardRef(({ onSubmit, getClassroomDetails }, ref) => {
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const lang = getLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [targetLesson, setTargetLesson] = useState(null);
  const [prerequisiteLesson, setPrerequisiteLesson] = useState(null);
  const [classroom, setClassroom] = useState(null);

  useImperativeHandle(ref, () => ({
    openModal(classroomData, lesson, prevLesson) {
      setClassroom(classroomData);
      setTargetLesson(lesson);
      setPrerequisiteLesson(prevLesson);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setTargetLesson(null);
    setIsOpen(false);
  };

  const handleToggleLessonLock = async () => {
    try {
      setLoading(true);
      const isCurrentlyUnlocked = targetLesson?.is_locked === 0;
      const idToSend = isCurrentlyUnlocked ? targetLesson?.lesson_id : prerequisiteLesson?.lesson_id;
      const response = await lessonServices.toggleLockLesson(classroom?.id, idToSend);
      await getClassroomDetails();
      showSnack(response.message, "success");
      onSubmit();
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  return (
    <Modal
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button
            color="white"
            variant="outlined"
            onClick={handleCloseModal}
            label={t("cancel")}
            className="w-28"
          />
          <Button label={t("yes_sure")} loading={loading} onClick={handleToggleLessonLock} className="w-32" />
        </div>
      }
      isOpen={isOpen}
      onClose={handleCloseModal}
      className="w-full max-w-lg"
    >
      <h3 className="text-2xl font-bold text-blue-500">
        {targetLesson?.is_locked === 0 ? t("confirm_lock") : t("confirm_unlock")}
      </h3>
      <p className="mt-4 text-lg">
        {targetLesson?.is_locked === 0 ? t("are_you_sure_lock") : t("are_you_sure_unlock")}{" "}
        <strong>
          {targetLesson?.is_locked === 1
            ? lang == "ar"
              ? prerequisiteLesson?.title_ar + t("question_mark")
              : prerequisiteLesson?.title_en + t("question_mark")
            : null}
        </strong>
      </p>
    </Modal>
  );
});

export default ConfirmUnlockLockLessonsModal;
